import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, CalendarPlus, Filter, MapPin, MessageSquare, Search, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SubjectSelector from '../../components/SubjectSelector';
import { CURRICULUM_OPTIONS } from '../../constants/curriculum';
import { useAuth } from '../../contexts/AuthContext';
import { composeLocation } from '../../utils/location';
import api from '../../services/api';
import { locationApi } from '../../services/location.service';
import {
  getStudentScopedStorageItem,
  SELECTED_TEACHER_PREFERENCE_STORAGE_KEY,
  SELECTED_TEACHERS_STORAGE_KEY,
  setStudentScopedStorageItem,
} from '../../utils/studentSelectionStorage';

interface Teacher {
  id: string;
  userId?: string;
  hasActiveSubscription?: boolean;
  name: string;
  subject: string;
  location: string;
  searchableLocation?: string;
  distanceKm?: number;
  rating: number;
  reviewCount?: number;
  completedClassCount?: number;
  responseRate?: number;
  demoConversionRate?: number;
  experienceYears?: number;
  availabilityScore?: number;
  matchScore?: number;
  rankingSignals?: string[];
  rate: number;
  image: string;
  about: string;
  experience: string;
  availabilitySlots?: string[];
  curricula?: string[];
}

declare global {
  interface Window {
    google?: any;
  }
}

const getTeacherKey = (teacher: Teacher) => String(teacher.userId || teacher.id || '').trim();
const SAVED_TUTORS_STORAGE_KEY = 'savedTutors';
const SEARCH_RADIUS_OPTIONS = [5, 10, 15, 20];

const defaultAvatarFor = (name?: string) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent((name || 'Teacher').trim())}&background=0D8ABC&color=fff`;

const normalizeLocationText = (value?: string) =>
  (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const TeacherCard = memo(function TeacherCard({
  teacher,
  isSelected,
  isSaved,
  distanceKm,
  onViewProfile,
  onRequestDemo,
  onToggleSave,
}: {
  teacher: Teacher;
  isSelected: boolean;
  isSaved: boolean;
  distanceKm?: number;
  onViewProfile: (teacher: Teacher) => void;
  onRequestDemo: (teacher: Teacher) => void;
  onToggleSave: (teacher: Teacher) => void;
}) {
  const lockTeacherDetails = !teacher.hasActiveSubscription;
  const fullStars = Math.floor(teacher.rating || 0);
  const nextSlot = Array.isArray(teacher.availabilitySlots) ? teacher.availabilitySlots[0] : '';
  const derivedReviewCount = useMemo(() => {
    const teacherKey = getTeacherKey(teacher);
    const base = teacherKey
      .split('')
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return (base % 100) + 1;
  }, [teacher]);
  const reviewCount = Number.isFinite(teacher.reviewCount) ? Number(teacher.reviewCount) : derivedReviewCount;
  const matchScore = Number.isFinite(teacher.matchScore) ? Math.round(Number(teacher.matchScore)) : undefined;
  const rankingSignals = Array.isArray(teacher.rankingSignals) ? teacher.rankingSignals.slice(0, 4) : [];

  return (
    <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:shadow-[0_24px_55px_rgba(15,23,42,0.10)]">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col gap-3 md:flex-row md:gap-4">
          <div className="flex items-start gap-3 self-start md:flex-shrink-0">
            <img
              className="h-16 w-16 rounded-xl object-cover shadow-lg sm:h-24 sm:w-24 sm:rounded-2xl md:h-32 md:w-32 md:rounded-[28px]"
              src={teacher.image || defaultAvatarFor(teacher.name)}
              alt={teacher.name}
              onError={(event) => {
                const target = event.currentTarget;
                const fallback = defaultAvatarFor(teacher.name);
                if (target.src !== fallback) {
                  target.src = fallback;
                }
              }}
            />
            <div className="md:hidden">
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">
                {lockTeacherDetails ? 'Teacher Locked' : teacher.name}
              </h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">
                {teacher.subject}
              </p>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="hidden text-lg font-semibold tracking-tight text-slate-900 sm:text-2xl md:block">
                {lockTeacherDetails ? 'Teacher Locked' : teacher.name}
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {isSelected && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                    Selected
                  </span>
                )}
                {!lockTeacherDetails && (
                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 sm:text-sm">
                    Rs {teacher.rate}/hr
                  </span>
                )}
                {matchScore !== undefined && (
                  <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-800 sm:text-sm">
                    {matchScore}% match
                  </span>
                )}
              </div>
            </div>
            <p className="mt-2 hidden text-sm font-semibold uppercase tracking-[0.18em] text-indigo-700 md:block">
              {teacher.subject}
            </p>
            {!lockTeacherDetails ? (
              <p className="mt-2 text-sm text-slate-600">{teacher.location}</p>
            ) : (
              <p className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                Name and location are locked. Only rating and subject are shown.
              </p>
            )}
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex items-center">
                {Array.from({ length: 5 }, (_, index) => (
                  <Star
                    key={index}
                    className={`h-4 w-4 sm:h-5 sm:w-5 ${
                      index < fullStars ? 'text-yellow-400' : 'text-gray-300'
                    }`}
                    fill={index < fullStars ? 'currentColor' : 'none'}
                  />
                ))}
                <span className="ml-1 text-xs text-gray-600 sm:text-sm">
                  {teacher.rating} ({reviewCount} verified reviews)
                </span>
              </div>
              {Number.isFinite(distanceKm) && (
                <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-700 sm:ml-4 sm:text-xs">
                  {distanceKm?.toFixed(1)} km away
                </span>
              )}
            </div>
            {!lockTeacherDetails && (
              <p className="mt-3 max-w-3xl text-xs leading-5 text-slate-600 sm:text-sm sm:leading-6">
                {teacher.about}
              </p>
            )}
            {!lockTeacherDetails && (
              <div className="mt-4 flex flex-wrap gap-2">
                {rankingSignals.map((signal) => (
                  <span key={signal} className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {signal}
                  </span>
                ))}
                <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                  {teacher.experience} experience
                </span>
                {Array.isArray(teacher.curricula) &&
                  teacher.curricula.map((item) => (
                    <span key={item} className="inline-flex items-center rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
                      {item}
                    </span>
                  ))}
                {nextSlot && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                    Next slot {nextSlot.replace('|', ' ')}
                  </span>
                )}
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Available Online
                </span>
                <span className="inline-flex items-center rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                  Free Trial
                </span>
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => onViewProfile(teacher)}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.18)] transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                View Profile
              </button>
              {!lockTeacherDetails && (
                <>
                  <button
                    type="button"
                    onClick={() => onRequestDemo(teacher)}
                    className="inline-flex items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 sm:ml-3"
                  >
                    <CalendarPlus className="mr-2 h-4 w-4" />
                    Request Demo
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggleSave(teacher)}
                    className={`inline-flex items-center justify-center rounded-2xl border px-4 py-2.5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      isSaved
                        ? 'border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Bookmark className="mr-2 h-4 w-4" fill={isSaved ? 'currentColor' : 'none'} />
                    {isSaved ? 'Saved' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Message
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const FindTeacher: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mapLocation, setLocation] = useState('');
  const [searchCoordinates, setSearchCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [stateName, setStateName] = useState('');
  const [district, setDistrict] = useState('');
  const [mandal, setMandal] = useState('');
  const [village, setVillage] = useState('');
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);
  const [mandalOptions, setMandalOptions] = useState<string[]>([]);
  const [villageOptions, setVillageOptions] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [curriculum, setCurriculum] = useState('');
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [detectMessage, setDetectMessage] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<Set<string>>(new Set());
  const [savedTeacherIds, setSavedTeacherIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    minRating: 0,
    maxRate: 1000,
    experience: 'all',
    maxDistanceKm: 20,
  });
  const currentStudentUserId = String(user?.id || '').trim();

  const mapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const markerRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const hasGoogleMapsKey = Boolean((process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim());

  const selectedLocation = useMemo(
    () => composeLocation(stateName, district, mandal, village),
    [stateName, district, mandal, village],
  );
  const effectiveLocation = selectedLocation || mapLocation;
  const searchLocationText = selectedLocation || (!searchCoordinates ? mapLocation : '');
  const canSearchByLocation = Boolean((stateName && district) || searchCoordinates || mapLocation.trim());

  const updateMapMarker = useCallback((lat: number, lng: number, title: string) => {
    setSearchCoordinates({ lat, lng });

    if (!window.google || !mapRef.current) return;
    const map = (mapRef.current as any).__map;
    if (!map) return;

    map.setCenter({ lat, lng });
    map.setZoom(15);

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map,
        position: { lat, lng },
        title,
        animation: window.google.maps.Animation.DROP,
      });
      return;
    }

    markerRef.current.setPosition({ lat, lng });
    markerRef.current.setTitle(title);
  }, []);

  const matchFromOptions = useCallback((options: string[], candidates: string[]): string => {
    for (const candidate of candidates) {
      const normalizedCandidate = normalizeLocationText(candidate);
      if (!normalizedCandidate) continue;
      const direct = options.find((option) => normalizeLocationText(option) === normalizedCandidate);
      if (direct) return direct;
      const partial = options.find((option) =>
        normalizeLocationText(option).includes(normalizedCandidate) ||
        normalizedCandidate.includes(normalizeLocationText(option)),
      );
      if (partial) return partial;
    }
    return '';
  }, []);

  const inferHierarchyFromAddress = useCallback((
    address: Record<string, string>,
  ): Promise<{ state: string; district: string; mandal: string; village: string }> => (async () => {
    const states = stateOptions.length ? stateOptions : await locationApi.getStates();
    const inferredState =
      matchFromOptions(states, [address.state, address.region, address.state_district]) || states[0] || '';
    const districts = inferredState ? await locationApi.getDistricts(inferredState) : [];
    const inferredDistrict = matchFromOptions(districts, [
      address.county,
      address.state_district,
      address.city_district,
      address.city,
      address.town,
    ]);
    const mandals =
      inferredState && inferredDistrict
        ? await locationApi.getMandals(inferredState, inferredDistrict)
        : [];
    const inferredMandal = matchFromOptions(mandals, [
      address.suburb,
      address.city_district,
      address.village,
      address.town,
      address.municipality,
    ]);
    const villages =
      inferredState && inferredDistrict && inferredMandal
        ? await locationApi.getVillages(inferredState, inferredDistrict, inferredMandal)
        : [];
    const inferredVillage = matchFromOptions(villages, [
      address.village,
      address.suburb,
      address.hamlet,
      address.neighbourhood,
      address.town,
      address.city,
    ]);

    return {
      state: inferredState,
      district: inferredDistrict,
      mandal: inferredMandal,
      village: inferredVillage,
    };
  })(), [matchFromOptions, stateOptions]);

  const loadSelectedTeachers = useCallback(async () => {
    const ids = new Set<string>();

    if (currentStudentUserId) {
      try {
        const response = await api.get<Array<{ teacherId?: string; teacher?: { userId?: string; id?: string } }>>(
          '/teachers/selections/student',
        );
        if (Array.isArray(response.data)) {
          response.data.forEach((item) => {
            const teacherKey = String(
              item?.teacherId || item?.teacher?.userId || item?.teacher?.id || '',
            ).trim();
            if (teacherKey) ids.add(teacherKey);
          });
        }
      } catch (error) {
        console.warn('Failed to load selected teachers from API', error);
      }
    }

    const selectedTeachersRaw = getStudentScopedStorageItem(
      SELECTED_TEACHERS_STORAGE_KEY,
      currentStudentUserId,
    );
    const selectedTeacherPreferenceRaw = getStudentScopedStorageItem(
      SELECTED_TEACHER_PREFERENCE_STORAGE_KEY,
      currentStudentUserId,
    );

    if (ids.size === 0 && selectedTeachersRaw) {
      try {
        const selectedTeachers = JSON.parse(selectedTeachersRaw);
        if (Array.isArray(selectedTeachers)) {
          selectedTeachers.forEach((item: any) => {
            const teacherKey = String(item?.teacher?.userId || item?.teacher?.id || '').trim();
            if (teacherKey) ids.add(teacherKey);
          });
        }
      } catch (error) {
        console.warn('Failed to parse selectedTeachers from localStorage', error);
      }
    }

    if (ids.size === 0 && selectedTeacherPreferenceRaw) {
      try {
        const selectedTeacherPreference = JSON.parse(selectedTeacherPreferenceRaw);
        const teacherKey = String(
          selectedTeacherPreference?.teacher?.userId || selectedTeacherPreference?.teacher?.id || '',
        ).trim();
        if (teacherKey) ids.add(teacherKey);
      } catch (error) {
        console.warn('Failed to parse selectedTeacherPreference from localStorage', error);
      }
    }

    setSelectedTeacherIds(ids);
  }, [currentStudentUserId]);

  const loadSavedTeachers = useCallback(() => {
    const savedRaw = getStudentScopedStorageItem(SAVED_TUTORS_STORAGE_KEY, currentStudentUserId);
    if (!savedRaw) {
      setSavedTeacherIds(new Set());
      return;
    }

    try {
      const savedTeachers = JSON.parse(savedRaw);
      if (!Array.isArray(savedTeachers)) {
        setSavedTeacherIds(new Set());
        return;
      }

      setSavedTeacherIds(
        new Set(
          savedTeachers
            .map((item: { userId?: string; id?: string }) => String(item?.userId || item?.id || '').trim())
            .filter(Boolean),
        ),
      );
    } catch (error) {
      console.warn('Failed to parse saved tutors from localStorage', error);
      setSavedTeacherIds(new Set());
    }
  }, [currentStudentUserId]);

  const fetchTeachers = useCallback(async (useCurrentFilters: boolean = false) => {
    const params = useCurrentFilters
      ? {
          location: searchLocationText || undefined,
          subject: subject.trim() || undefined,
          curriculum: curriculum || undefined,
          minRating: Number.isFinite(filters.minRating) ? filters.minRating : undefined,
          maxRate: Number.isFinite(filters.maxRate) ? filters.maxRate : undefined,
          experience: filters.experience === 'all' ? undefined : filters.experience,
          latitude: searchCoordinates?.lat,
          longitude: searchCoordinates?.lng,
          maxDistanceKm:
            (effectiveLocation || searchCoordinates) && Number.isFinite(filters.maxDistanceKm) ? filters.maxDistanceKm : undefined,
        }
      : undefined;

    setIsLoading(true);
    try {
      const response = await api.get<Teacher[]>('/teachers/discover', { params });
      const teacherList = [...(response.data || [])].sort((a, b) => {
        const aScore = Number.isFinite(a.matchScore) ? Number(a.matchScore) : undefined;
        const bScore = Number.isFinite(b.matchScore) ? Number(b.matchScore) : undefined;
        if (aScore !== undefined || bScore !== undefined) {
          const scoreDiff = (bScore || 0) - (aScore || 0);
          if (scoreDiff !== 0) return scoreDiff;
        }
        const aDistance = Number.isFinite(a.distanceKm) ? Number(a.distanceKm) : Number.POSITIVE_INFINITY;
        const bDistance = Number.isFinite(b.distanceKm) ? Number(b.distanceKm) : Number.POSITIVE_INFINITY;
        if (aDistance !== bDistance) return aDistance - bDistance;
        return (b.rating || 0) - (a.rating || 0);
      });
      setFilteredTeachers(teacherList);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setFilteredTeachers([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveLocation, curriculum, filters.experience, filters.maxDistanceKm, filters.maxRate, filters.minRating, searchCoordinates, searchLocationText, subject]);

  useEffect(() => {
    if (user?.role === 'TEACHER') {
      navigate('/teacher/dashboard', { replace: true });
      return;
    }
    void fetchTeachers(false);
    void loadSelectedTeachers();
    loadSavedTeachers();
  }, [fetchTeachers, loadSavedTeachers, loadSelectedTeachers, navigate, user?.role]);

  useEffect(() => {
    const loadStates = async () => {
      setStateOptions(await locationApi.getStates());
    };
    void loadStates();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!stateName) {
        setDistrictOptions([]);
        return;
      }
      setDistrictOptions(await locationApi.getDistricts(stateName));
    };
    void loadDistricts();
  }, [stateName]);

  useEffect(() => {
    const loadMandals = async () => {
      if (!stateName || !district) {
        setMandalOptions([]);
        return;
      }
      setMandalOptions(await locationApi.getMandals(stateName, district));
    };
    void loadMandals();
  }, [district, stateName]);

  useEffect(() => {
    const loadVillages = async () => {
      if (!stateName || !district || !mandal) {
        setVillageOptions([]);
        return;
      }
      setVillageOptions(await locationApi.getVillages(stateName, district, mandal));
    };
    void loadVillages();
  }, [district, mandal, stateName]);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('Google Maps API key is not configured. Some features may be limited.');
      return;
    }

    const loadScript = (url: string) =>
      new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${url}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.head.appendChild(script);
      });

    void (async () => {
      try {
        await loadScript(`https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`);

        if (!window.google) return;

        geocoderRef.current = new window.google.maps.Geocoder();

        if (inputRef.current) {
          const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ['geocode', 'establishment'],
            fields: ['formatted_address', 'geometry', 'name', 'address_components'],
            componentRestrictions: { country: 'in' },
          });

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place.geometry || !place.geometry.location) {
              console.log(`No details available for input: ${place.name}`);
              return;
            }

            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const displayAddress = place.formatted_address || place.name || 'Selected location';
            setLocation(displayAddress);
            updateMapMarker(lat, lng, displayAddress);
          });
        }

        if (mapRef.current && !(mapRef.current as any).__map) {
          const map = new window.google.maps.Map(mapRef.current, {
            center: { lat: 20.5937, lng: 78.9629 },
            zoom: 5,
          });
          (mapRef.current as any).__map = map;

          map.addListener('click', (event: any) => {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            updateMapMarker(lat, lng, 'Selected map location');

            if (geocoderRef.current) {
              geocoderRef.current.geocode(
                { location: { lat, lng } },
                (results: any, status: string) => {
                  if (status === 'OK' && results && results[0]) {
                    const address = results[0].formatted_address;
                    setLocation(address);
                    if (inputRef.current) inputRef.current.value = address;
                    if (markerRef.current) {
                      markerRef.current.setTitle(address);
                    }
                    return;
                  }

                  const coordString = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                  setLocation(coordString);
                  if (inputRef.current) inputRef.current.value = coordString;
                },
              );
            } else {
              const coordString = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
              setLocation(coordString);
              if (inputRef.current) inputRef.current.value = coordString;
            }
          });
        }
      } catch (error) {
        console.warn('Google Maps script failed to load or is not configured.', error);
      }
    })();
  }, [updateMapMarker]);

  const handleSearch = useCallback(async () => {
    if (!canSearchByLocation) {
      alert('Please select state and district, type a map location, or use current location first.');
      return;
    }
    await fetchTeachers(true);
  }, [canSearchByLocation, fetchTeachers]);

  const handleFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]:
        name === 'minRating' || name === 'maxRate' || name === 'maxDistanceKm'
          ? Number(value)
          : value,
    }));
  }, []);

  const handleViewProfile = useCallback((teacher: Teacher) => {
    navigate(`/student/teachers/${getTeacherKey(teacher)}/select`, { state: { teacher } });
  }, [navigate]);

  const handleRequestDemo = useCallback((teacher: Teacher) => {
    navigate(`/student/teachers/${getTeacherKey(teacher)}/select`, {
      state: { teacher, requestMode: 'DEMO' },
    });
  }, [navigate]);

  const handleToggleSave = useCallback((teacher: Teacher) => {
    const teacherKey = getTeacherKey(teacher);
    if (!teacherKey) return;

    const savedRaw = getStudentScopedStorageItem(SAVED_TUTORS_STORAGE_KEY, currentStudentUserId);
    let savedTeachers: Teacher[] = [];
    try {
      const parsed = savedRaw ? JSON.parse(savedRaw) : [];
      savedTeachers = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Failed to parse saved tutors before update', error);
    }

    const alreadySaved = savedTeachers.some((item) => getTeacherKey(item) === teacherKey);
    const nextSavedTeachers = alreadySaved
      ? savedTeachers.filter((item) => getTeacherKey(item) !== teacherKey)
      : [
          ...savedTeachers,
          {
            id: teacher.id,
            userId: teacher.userId,
            name: teacher.name,
            subject: teacher.subject,
            location: teacher.location,
            rating: teacher.rating,
            rate: teacher.rate,
            image: teacher.image,
            about: teacher.about,
            experience: teacher.experience,
            availabilitySlots: teacher.availabilitySlots,
            curricula: teacher.curricula,
          },
        ];

    setStudentScopedStorageItem(
      SAVED_TUTORS_STORAGE_KEY,
      currentStudentUserId,
      JSON.stringify(nextSavedTeachers),
    );
    setSavedTeacherIds(new Set(nextSavedTeachers.map((item) => getTeacherKey(item)).filter(Boolean)));
  }, [currentStudentUserId]);

  const handleSubjectChange = useCallback((value: string | string[]) => {
    setSubject(Array.isArray(value) ? value[0] || '' : value || '');
  }, []);

  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const handleDetectLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    try {
      setIsDetectingLocation(true);
      setDetectMessage('');
      const coords = await new Promise<{ lat: number; lng: number }>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) =>
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }),
          reject,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        );
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1`,
      );
      const data = await response.json();
      const inferred = await inferHierarchyFromAddress(data?.address || {});
      const displayAddress = data?.display_name || `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
      setLocation(displayAddress);
      if (inputRef.current) inputRef.current.value = displayAddress;
      updateMapMarker(coords.lat, coords.lng, displayAddress);

      if (inferred.state) setStateName(inferred.state);
      if (inferred.district) setDistrict(inferred.district);
      if (inferred.mandal) setMandal(inferred.mandal);
      if (inferred.village) setVillage(inferred.village);

      if (inferred.state && inferred.district && inferred.mandal && inferred.village) {
        setDetectMessage('Location detected and dropdowns were auto-filled.');
      } else {
        setDetectMessage(
          'Location detected partially. Please complete remaining dropdown fields manually.',
        );
      }
    } catch (error) {
      console.error('Failed to detect location:', error);
      setDetectMessage('Could not detect location. Please select manually.');
    } finally {
      setIsDetectingLocation(false);
    }
  }, [inferHierarchyFromAddress, updateMapMarker]);

  const distanceByTeacherId = useMemo(
    () =>
      filteredTeachers.reduce<Record<string, number>>((acc, teacher) => {
        if (Number.isFinite(teacher.distanceKm)) {
          acc[getTeacherKey(teacher)] = Number(teacher.distanceKm);
        }
        return acc;
      }, {}),
    [filteredTeachers],
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(79,70,229,0.12),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(16,185,129,0.10),_transparent_24%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)]">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 overflow-hidden rounded-[28px] border border-slate-200/70 bg-white/90 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="grid gap-6 p-6 lg:grid-cols-[1.15fr_0.85fr] lg:p-8">
            <div>
              <span className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-700">
                Nearby Discovery
              </span>
              <h1 className="mt-4 max-w-2xl font-serif text-4xl tracking-tight text-slate-900 sm:text-5xl">
                Find local teachers with distance-first search.
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                Search the way modern discovery apps should work: start with district, tune the radius, and rank teachers by proximity before anything else.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Radius</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{filters.maxDistanceKm} km</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Teachers</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{filteredTeachers.length}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Search Area</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {district ? `${district}, ${stateName}` : searchCoordinates ? 'Current location' : 'Choose district'}
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-[24px] bg-[linear-gradient(180deg,_#0f172a_0%,_#312e81_100%)] p-5 text-white shadow-[0_20px_50px_rgba(15,23,42,0.22)]">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-200">Primary Filter</p>
              <h2 className="mt-3 text-xl font-semibold">Distance comes first</h2>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                This product is built around nearby teacher discovery. Set the radius once and let the list prioritize local matches.
              </p>
              <div className="mt-5 rounded-2xl bg-white/5 px-4 py-4">
                <label className="block text-sm font-medium text-indigo-100">
                  Search radius
                </label>
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {SEARCH_RADIUS_OPTIONS.map((radius) => (
                    <button
                      key={radius}
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          maxDistanceKm: radius,
                        }))
                      }
                      className={`rounded-2xl border px-3 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
                        filters.maxDistanceKm === radius
                          ? 'border-emerald-300 bg-emerald-300 text-slate-950'
                          : 'border-white/10 bg-white/10 text-indigo-100 hover:bg-white/15'
                      }`}
                    >
                      {radius} km
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-300">
                  Recommended local discovery range is 5 km to 20 km.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-[28px] border border-slate-200/70 bg-white/90 p-6 shadow-[0_16px_60px_rgba(15,23,42,0.06)] backdrop-blur">
          <div className="mb-4 rounded-[22px] border border-indigo-100 bg-indigo-50 px-4 py-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-indigo-900">Nearby teachers first</p>
                <p className="text-xs text-indigo-700">
                  State and district are enough to search. Mandal and village are optional refinements.
                </p>
              </div>
              <div className="w-full md:w-72">
                <div className="rounded-2xl border border-indigo-200 bg-white px-4 py-3 text-sm font-semibold text-indigo-900">
                  Search within {filters.maxDistanceKm} km
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 rounded-[24px] border border-slate-200 bg-slate-50 p-4">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Map or current location</label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    ref={inputRef}
                    type="text"
                    onChange={(event) => {
                      setLocation(event.target.value);
                      setSearchCoordinates(null);
                    }}
                    className="min-h-[48px] flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Search an area, landmark, colony, town, or use current location"
                  />
                  <button
                    type="button"
                    onClick={handleDetectLocation}
                    disabled={isDetectingLocation}
                    className="inline-flex min-h-[48px] items-center justify-center rounded-2xl bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                  >
                    <MapPin className="mr-2 h-4 w-4" />
                    {isDetectingLocation ? 'Detecting...' : 'Use Current Location'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Current location and map clicks use coordinates for distance ranking. Manual dropdowns remain available below.
                </p>
                {searchCoordinates && (
                  <p className="mt-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Using coordinates: {searchCoordinates.lat.toFixed(4)}, {searchCoordinates.lng.toFixed(4)}
                  </p>
                )}
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                {hasGoogleMapsKey ? (
                  <div ref={mapRef} className="h-56 w-full" />
                ) : (
                  <div className="flex h-56 items-center justify-center px-4 text-center text-sm text-slate-500">
                    Add REACT_APP_GOOGLE_MAPS_API_KEY to enable the map. Current-location search still works.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="block text-xs font-medium text-gray-600">State</label>
                <button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={isDetectingLocation}
                  className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-60"
                >
                  <MapPin className="mr-1 h-3.5 w-3.5" />
                  {isDetectingLocation ? 'Detecting...' : 'Detect'}
                </button>
              </div>
              <select
                className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={stateName}
                onChange={(event) => {
                  setStateName(event.target.value);
                  setDistrict('');
                  setMandal('');
                  setVillage('');
                  setSearchCoordinates(null);
                }}
              >
                <option value="">Select state</option>
                {stateOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">District/City</label>
              <select
                className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={district}
                onChange={(event) => {
                  setDistrict(event.target.value);
                  setMandal('');
                  setVillage('');
                  setSearchCoordinates(null);
                }}
              >
                <option value="">Select district</option>
                {districtOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Mandal</label>
              <select
                className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                value={mandal}
                onChange={(event) => {
                  setMandal(event.target.value);
                  setVillage('');
                  setSearchCoordinates(null);
                }}
                disabled={!stateName || !district}
              >
                <option value="">Select mandal</option>
                {mandalOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Village</label>
              <select
                className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                value={village}
                onChange={(event) => {
                  setVillage(event.target.value);
                  setSearchCoordinates(null);
                }}
                disabled={!stateName || !district || !mandal}
              >
                <option value="">Select village</option>
                {villageOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-5">
            <div className="md:col-span-2">
              <SubjectSelector
                value={subject}
                onChange={handleSubjectChange}
                mode="single"
                placeholder="Select subject"
                className="w-full"
              />
            </div>
            <div className="md:col-span-2">
              <select
                value={curriculum}
                onChange={(event) => setCurriculum(event.target.value)}
                className="block h-[50px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All curricula</option>
                {CURRICULUM_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleSearch}
              disabled={isLoading || !canSearchByLocation}
              className="inline-flex h-[50px] items-center justify-center self-end rounded-2xl bg-slate-950 px-5 text-sm font-semibold text-white shadow-[0_14px_35px_rgba(15,23,42,0.18)] transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 md:min-w-[180px]"
            >
              <Search className="mr-2 h-4 w-4" />
              {isLoading ? 'Searching...' : 'Find Teachers'}
            </button>
          </div>
          {detectMessage && (
            <div className="mb-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
              {detectMessage}
            </div>
          )}

          <button
            onClick={toggleFilters}
            className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
          >
            <Filter className="mr-1 h-4 w-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          {showFilters && (
            <div className="mt-4 rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5">
              <div className="mb-4">
                <p className="text-sm font-semibold text-slate-900">Smart filters</p>
                <p className="text-xs text-slate-500">Apply these after location if you need tighter refinement.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Minimum Rating</label>
                  <select
                    name="minRating"
                    value={filters.minRating}
                    onChange={handleFilterChange}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={0}>Any Rating</option>
                    <option value={4}>4.0+</option>
                    <option value={4.5}>4.5+</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Max Hourly Rate (Rs)</label>
                  <input
                    type="number"
                    name="maxRate"
                    value={filters.maxRate}
                    onChange={handleFilterChange}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Experience</label>
                  <select
                    name="experience"
                    value={filters.experience}
                    onChange={handleFilterChange}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">Any Experience</option>
                    <option value="1-3 years">1-3 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="5+ years">5+ years</option>
                    <option value="10+ years">10+ years</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Results</p>
            <h3 className="mt-1 text-2xl font-semibold text-slate-900">Teachers ranked for fit</h3>
            <p className="mt-1 text-sm text-slate-600">Results balance distance, verified ratings, experience, response, conversion, and availability.</p>
          </div>
          <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
            {filteredTeachers.length} teacher{filteredTeachers.length === 1 ? '' : 's'} found
          </div>
        </div>

        <div className="space-y-6">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center rounded-[24px] border border-slate-200 bg-white/80">
              <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600"></div>
            </div>
          ) : filteredTeachers.length > 0 ? (
            filteredTeachers.map((teacher) => (
              <TeacherCard
                key={teacher.userId || teacher.id}
                teacher={teacher}
                isSelected={selectedTeacherIds.has(getTeacherKey(teacher))}
                isSaved={savedTeacherIds.has(getTeacherKey(teacher))}
                distanceKm={distanceByTeacherId[getTeacherKey(teacher)]}
                onViewProfile={handleViewProfile}
                onRequestDemo={handleRequestDemo}
                onToggleSave={handleToggleSave}
              />
            ))
          ) : (
            <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/70 px-6 py-16 text-center shadow-sm">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">No teachers found in this radius</h3>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                Expand the distance, adjust district filters, or relax the smart filters to discover more nearby teachers.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindTeacher;
